import React, {useEffect, useState} from "react";
import Loader from "../../components/Loader";
import axiosInstance from "../../utils/axiosInstance";
import {IoArrowBackSharp} from "react-icons/io5";

const EmailForm = () => {
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState("");
    const [emails, setEmails] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        const fetchEmails = async () => {
            try {
                const response = await axiosInstance.get("/emails");
                setEmails(response.data);
                setLoading(false);
            } catch (error) {
                console.error("Failed to fetch users:", error);
            }
        };
        fetchEmails();
    }, []);

    const handleCheckboxChange = (email) => {
        setSelectedUsers((prevSelectedUsers) => {
            if (prevSelectedUsers.includes(email)) {
                return prevSelectedUsers.filter((e) => e !== email);
            } else {
                return [...prevSelectedUsers, email];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(emails);
        }
        setSelectAll(!selectAll);
    };

    const handleSendEmail = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await axiosInstance.post("/emails/send", {
                subject,
                body,
                recipients: selectedUsers,
            });
            if (response.status === 200) {
                setMessage("Emails sent successfully!");
            }
        } catch (error) {
            setMessage("Failed to send emails.");
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSendEmailAsync = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const response = await axiosInstance.post("/emails/send/async", {
                subject,
                body,
                recipients: selectedUsers,
            });
            if (response.status === 200) {
                setMessage("Emails async started successfully!");
            }
        } catch (error) {
            setMessage("Failed to start async emails send process.");
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const filteredEmails = emails.filter((email) =>
        email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="my-12 flex justify-center">
            <div className="w-full max-w-md p-6 rounded-[22px] border border-gray-200 bg-white shadow-lg shadow-gray-900/5 dark:border-[#2c2c2e] dark:bg-[#1c1c1e] dark:shadow-black/40">
                <h3 className="mb-1 flex items-center gap-3 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                    {/* Go Back Button */}
                    <button
                        onClick={() => window.history.back()} // This will navigate back to the previous page
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-500 transition hover:border-red-400/60 hover:text-red-500 dark:border-[#3a3a3d] dark:text-slate-400"
                    >
                        <IoArrowBackSharp size={24}/>
                    </button>
                <span className="h-6 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-red-500 to-orange-400" />
                    Send Email to Selected Users
                </h3>
                {message && (
                    <p
                        className={`text-center mb-4 ${
                            message.includes("successfully")
                                ? "text-green-600"
                                : "text-red-600"
                        }`}
                    >
                        {message}
                    </p>
                )}
                <form onSubmit={handleSendEmail}>
                    <div className="mb-4">
                        <label
                            htmlFor="subject"
                            className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
                        >
                            Subject
                        </label>
                        <input
                            type="text"
                            id="subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
                            required
                        />
                    </div>
                    <div className="mb-4">
                        <label
                            htmlFor="body"
                            className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
                        >
                            Body (supports HTML)
                        </label>
                        <textarea
                            id="body"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            rows={6}
                            className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
                            required
                        />
                    </div>
                    <div className="mb-4">
                        <input
                            type="checkbox"
                            id="select-all"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="mr-2"
                        />
                        <label htmlFor="select-all" className="text-sm text-gray-700 dark:text-slate-300">
                            Select All
                        </label>

                        {/* Search bar */}
                        <div className="mt-2 mb-2">
                            <input
                                type="text"
                                placeholder="Search emails..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
                            />
                        </div>

                        {/* Email list */}
                        <div className="h-32 overflow-y-auto rounded-[12px] border border-gray-200 p-2 dark:border-[#2c2c2e]">
                            {loading ? (
                                <Loader />
                            ) : filteredEmails.length > 0 ? (
                                filteredEmails.map((email) => (
                                    <div key={email} className="flex items-center mb-2">
                                        <input
                                            type="checkbox"
                                            id={`user-${email}`}
                                            checked={selectedUsers.includes(email)}
                                            onChange={() => handleCheckboxChange(email)}
                                            className="mr-2 cursor-pointer"
                                        />
                                        <label htmlFor={`user-${email}`} className="text-sm text-gray-700 dark:text-slate-300">
                                            {email}
                                        </label>
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500 text-sm">No results found</p>
                            )}
                        </div>
                    </div>

                    {/* Normal send button */}
                    <button
                        type="submit"
                        className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-600 to-orange-500 px-6 text-sm font-bold text-white shadow-lg shadow-red-500/25 transition hover:from-red-500 hover:to-orange-400 disabled:cursor-not-allowed disabled:opacity-60 w-full mb-2"
                        disabled={isSubmitting || selectedUsers.length === 0}
                    >
                        {isSubmitting ? "Sending..." : "Send Email"}
                    </button>

                    {/* Async send button */}
                    <button
                        type="button"
                        onClick={handleSendEmailAsync}
                        className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-emerald-500 px-6 text-sm font-bold text-white transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60 w-full"
                        disabled={isSubmitting || selectedUsers.length === 0}
                    >
                        {isSubmitting ? "Starting Async..." : "Send Email Async"}
                    </button>
                </form>

            </div>
        </div>
    );
};

export default EmailForm;
